import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { messageOf } from '../lib/errors'
import { useHubLang } from '../lib/i18n'
import { SUPPORT, supportStarLevelWords } from '../lib/hubCopy'
import type { Me } from '../lib/me'
import { can, canGallery } from '../lib/me'
import { canReachShopAdmin } from '../lib/shop'
import { supabase } from '../lib/supabase'
import { AppShell } from '../components/AppShell'
import { Card } from '../components/Card'
import { SupportThread } from '../components/SupportThread'
import { SupportStaffPanel } from '../components/SupportStaffPanel'
import {
  addSupportNote,
  draftSupportAnswer,
  loadSupportStaffView,
  requestWikiDraft,
  sendToDevelopers,
  type SupportStaffView,
} from '../lib/supportStaff'
import { addComment, loadComments, type WikiComment } from '../lib/wiki'
import { awardSupportStars, loadSupportStars, type SupportStars } from '../lib/supportStars'
import { loadSavedReplies, repliesForLane, type SavedReply } from '../lib/supportReplies'
import {
  closeSupportQuestion,
  handSupportQuestion,
  laneWords,
  loadSupportLanes,
  loadSupportThread,
  moveSupportQuestion,
  moveTargets,
  myLanes,
  reopenSupportQuestion,
  requestSupportTranslation,
  sayItAnswered,
  type SupportLane,
  type SupportThreadHead,
} from '../lib/support'

/**
 * `#/support/:id` — one private thread. HLP-22.
 *
 * ── THIS IS THE ROUTER'S PATH-PARAMETER CASE, TAKEN ─────────────────────────
 *
 * CLAUDE.md has carried "the router's path-parameter case is still not taken —
 * do not add a fifth flat route and re-argue it" since WIK-17 and FED-18b both
 * wanted it. It was never expensive: `react-router-dom` has been the router
 * since 14 Sep, so the whole cost is one `<Route path="/support/:id">` and the
 * `useParams` below. Nobody had needed it enough to be the first.
 *
 * It is needed here because a NOTIFICATION WITH NOWHERE TO GO is the fault this
 * issue exists to fix, and the bell links on the kind alone — so `support.*`
 * had to have a destination that carries the id it was handed. A flat
 * `#/support` that ignored the id would be the same fault wearing a link.
 *
 * WIK-17 and FED-18b now have a worked shape to copy rather than an argument to
 * have. `notificationRoute()` in lib/moderation.ts is where a content permalink
 * would attach when they land.
 *
 * ── THE SERVER SAYS WHICH SIDE YOU ARE ON ───────────────────────────────────
 *
 * `head.iAsked` comes from `support_question()`. Nothing here compares person
 * ids to work it out — that would be a second answer to a question 089's read
 * policy already settles, and the two would disagree silently.
 */
export function SupportThreadRoute({ me }: { me: Me }) {
  const lang = useHubLang()
  const t = SUPPORT[lang]
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const lanes = myLanes(me)

  const [head, setHead] = useState<SupportThreadHead | null>(null)
  const [replies, setReplies] = useState<WikiComment[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // 117. The staff panel. Loaded only when the session holds this question's
  // lane — a presentation convenience; `support_staff_view()` refuses anybody
  // else, and a refusal is rendered in the panel rather than swallowed.
  const [staff, setStaff] = useState<SupportStaffView | null>(null)
  const [staffError, setStaffError] = useState<string | null>(null)
  const [staffBusy, setStaffBusy] = useState(false)
  // 119. The staff column is for a holder of THIS lane, or the one person the
  // question was handed to — `i_am_assignee` comes from the server, never from
  // comparing ids here. `holder` is the narrower of the two: handing ON and
  // sending to developers stay the lane's.
  const holder = !!head && lanes.includes(head.lane)
  const isStaff = holder || (!!head && head.iAmAssignee)
  /* 121. The OPEN lanes, for "move to another lane"; `undefined` until the
   * server answers, which `moveTargets()` treats as nowhere (the drawer's
   * posture). Loaded only for a holder: nobody else is drawn the door. */
  const [openLanes, setOpenLanes] = useState<SupportLane[] | undefined>(undefined)
  useEffect(() => {
    if (!holder) return
    let live = true
    loadSupportLanes()
      .then((rows) => live && setOpenLanes(rows.map((r) => r.lane)))
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [holder])
  /* 122. The lane's saved replies, for the picker. Loaded only for a holder
   * (the read refuses everybody else, and nobody else is drawn the picker). */
  const [saved, setSaved] = useState<SavedReply[]>([])
  useEffect(() => {
    if (!holder || !head) return
    let live = true
    loadSavedReplies(head.lane)
      .then((rows) => live && setSaved(rows))
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [holder, head?.lane])

  /**
   * 121. After a move this lane may no longer be able to read the thread — the
   * read policy follows the lane — so this route goes back to the desk rather
   * than re-reading a thread the server will refuse.
   */
  async function move(lane: SupportLane, reason: string) {
    if (!id) return
    setStaffBusy(true)
    setStaffError(null)
    try {
      await moveSupportQuestion(id, lane, reason)
      navigate('/support')
    } catch (e) {
      setStaffError(messageOf(e))
    } finally {
      setStaffBusy(false)
    }
  }

  /* 172. Stars on a platform ticket — for the asker (their line) and for
   * whoever staffs it (the panel). The server refuses anybody else. */
  const [stars, setStars] = useState<SupportStars | null>(null)
  const starsFor = !!id && !!head && head.lane === 'platform' && (head.iAsked || isStaff)
  useEffect(() => {
    if (!id || !starsFor) return
    let live = true
    loadSupportStars(id)
      .then((s) => live && setStars(s))
      .catch(() => live && setStars(null))
    return () => {
      live = false
    }
  }, [id, starsFor])

  useEffect(() => {
    if (!id || !isStaff) return
    let live = true
    loadSupportStaffView(id)
      .then((v) => live && (setStaff(v), setStaffError(null)))
      .catch((e: Error) => live && setStaffError(e.message))
    return () => {
      live = false
    }
  }, [id, isStaff])

  async function staffAct(act: () => Promise<void>) {
    if (!id) return
    setStaffBusy(true)
    setStaffError(null)
    try {
      await act()
      // 123. A note, a hand or a move may need a rendering for a new reader.
      void requestSupportTranslation(id)
      // Re-read rather than splice: the server may have refused half of it.
      // The HEAD too (119): a hand-off changes who the thread says has it.
      const [v, h] = await Promise.all([loadSupportStaffView(id), loadSupportThread(id)])
      setStaff(v)
      setHead(h)
    } catch (e) {
      setStaffError(messageOf(e))
    } finally {
      setStaffBusy(false)
    }
  }

  async function refresh(questionId: string) {
    // The head is re-read as well as the replies, because the verdict lives on
    // it: splicing a local copy after a write is how a screen shows a state the
    // database refused (Moderation.tsx's reason).
    const [h, c] = await Promise.all([loadSupportThread(questionId), loadComments(questionId)])
    setHead(h)
    setReplies(c)
  }

  useEffect(() => {
    if (!id) return
    let live = true
    Promise.all([loadSupportThread(id), loadComments(id)])
      .then(([h, c]) => {
        if (!live) return
        setHead(h)
        setReplies(c)
        setError(null)
      })
      .catch((e: Error) => live && setError(e.message))
    // 123. Ask for any rendering this thread is still missing in a reader's
    // language; re-read only if something was stored. Idempotent and cheap.
    void requestSupportTranslation(id).then((stored) => {
      if (live && stored.length > 0) refresh(id).catch(() => undefined)
    })
    return () => {
      live = false
    }
  }, [id])

  async function reply(body: string, andClose = false) {
    if (!id) return
    setBusy(true)
    setError(null)
    try {
      await addComment(id, body)
      void requestSupportTranslation(id)
      // 174. Reply and close: the close is a second act, after the reply.
      if (andClose) await closeSupportQuestion(id)
      await refresh(id)
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  /** 174. The lane closes it (or reopens it); the asker is asked to confirm. */
  async function closeOrReopen(reopen: boolean) {
    if (!id) return
    setBusy(true)
    setError(null)
    try {
      if (reopen) await reopenSupportQuestion(id)
      else await closeSupportQuestion(id)
      await refresh(id)
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  async function verdict(answered: boolean) {
    if (!id) return
    setBusy(true)
    setError(null)
    try {
      await sayItAnswered(id, answered)
      await refresh(id)
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  const shell = (children: React.ReactNode) => (
    <AppShell
      current={head && !head.iAsked ? 'Support inbox' : 'Your questions'}
      canAdmin={can(me, 'admin.roles')}
      canShop={canReachShopAdmin(me)}
      canModerate={can(me, 'content.moderate')}
      canStarsAdmin={can(me, 'stars.admin')}
      canPhotoRoom={canGallery(me)}
      canAnalytics={can(me, 'analytics.read')}
      canNotices={can(me, 'content.publish')}
      canAudit={can(me, 'audit.read')}
      supportLanes={lanes}
      userName={me.displayName}
      userInitials={initialsOf(me.displayName)}
      userRole={me.roles[0]?.name ?? 'No role'}
      locale={me.locale}
      onSignOut={() => supabase.auth.signOut()}
    >
      {children}
    </AppShell>
  )

  if (error) {
    /* THE REFUSAL IS RENDERED, NOT SWALLOWED. `support_question()` raises for a
       thread that is not yours rather than returning nothing (rule 12), and the
       sentence it raises is the SAME one it uses for an id that does not exist
       — deliberately, so this screen cannot be used to find out whether a given
       uuid is somebody's private question. */
    return shell(
      <Card title={t.privateQuestion}>
        <p className="empty">{error}</p>
      </Card>,
    )
  }

  if (!head) {
    return shell(
      <Card title={t.privateQuestion}>
        <p className="empty">{t.opening}</p>
      </Card>,
    )
  }

  return shell(
    <Card title={t.privateSuffix(laneWords(head.lane, lang).label)}>
      <SupportThread
        head={head}
        replies={replies}
        onReply={(body) => void reply(body)}
        // 118. The team note is the composer's second mode, for a holder of
        // THIS lane only — `add_support_note` refuses anybody else (117).
        onNote={isStaff ? (b) => void staffAct(() => addSupportNote(head.questionId, b)) : undefined}
        // 122. Saved replies for a HOLDER of this lane only.
        savedReplies={holder ? repliesForLane(saved, head.lane) : []}
        // Wired only for the asker. The SERVER refuses everybody else including
        // the lane holder (rule 21); this is presentation on top of that, not
        // the gate — see SupportThread.tsx.
        onVerdict={head.iAsked ? (a) => void verdict(a) : undefined}
        // 174. The lane's close — whoever staffs it; the server checks.
        onClose={isStaff ? () => void closeOrReopen(false) : undefined}
        onReopen={isStaff ? () => void closeOrReopen(true) : undefined}
        onReplyAndClose={isStaff ? (body) => void reply(body, true) : undefined}
        onBack={() => navigate('/support')}
        busy={busy || staffBusy}
      />
      {/* 172. The asker's line: what platform support made of the ticket. */}
      {head.iAsked && stars?.level && (
        <p className="supportq__stars" role="status">
          {t.starsOnTicket(supportStarLevelWords(lang, stars.level), stars.starsPaid)}
        </p>
      )}
      {isStaff && staff && (
        <SupportStaffPanel
          // Keyed on the handoff so a re-read after Send resets the form to
          // what the server now holds, not what was typed.
          key={`${staff.handoff?.requestedAt ?? 'none'}-${staff.handoff?.trackerIssueId ?? ''}-${staff.wikiDraft?.draftedAt ?? ''}-${staff.wikiAnswer?.state ?? ''}`}
          view={staff}
          onSend={holder ? (t2, b) => void staffAct(() => sendToDevelopers(head.questionId, t2, b)) : undefined}
          handed={{ to: head.assignedTo, at: head.assignedAt, iAmAssignee: head.iAmAssignee }}
          onHand={(p, n) => void staffAct(() => handSupportQuestion(head.questionId, p, n))}
          canHandOn={holder}
          // 120. Anybody staffing it may ask for the machine's draft; only a HOLDER
          // confirms it into Wiki One (the server refuses the assignee).
          onWikiDraft={() => void staffAct(async () => { await requestWikiDraft(head.questionId) })}
          onWikiConfirm={holder ? (q, a) => void staffAct(async () => { await draftSupportAnswer(head.questionId, q, a) }) : undefined}
          // 121. Moving lanes is the HOLDER's (the server refuses the assignee).
          moveTargets={holder ? moveTargets(head.lane, openLanes) : []}
          onMove={holder ? (l, r) => void move(l, r) : undefined}
          // 172. Stars: the HOLDER awards (the server refuses the assignee and the asker).
          stars={stars}
          onAward={holder ? (lvl) => void staffAct(async () => {
            await awardSupportStars(head.questionId, lvl)
            setStars(await loadSupportStars(head.questionId))
          }) : undefined}
          busy={staffBusy}
          error={staffError}
        />
      )}
      {isStaff && !staff && staffError && <p className="roles__error" role="alert">{staffError}</p>}
    </Card>,
  )
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}
